/**
 * Mojo Agent chat panel — site-wide AI financial assistant (012).
 */
import React, { useState, useRef, useEffect } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { getCurrentUserId } from '../../lib/supabase';
import { AiChatHistoryService, sendMessage } from '../../services/ai-chat-history-service';
import { useIsAiOperational } from '../../hooks/useIsAiOperational';
import {
  Send,
  Loader2,
  AlertCircle,
  MoreVertical,
  Trash2,
  Download,
  Bot,
  X,
  Pin,
  PinOff,
  Maximize2,
  Minimize2,
} from 'lucide-react';
import { useMojoAgent } from '../../contexts/MojoAgentContext';
import { format } from 'date-fns';
import type { ChatMessageRow } from '../../types/ai-chat.types';
import { ChatCategorySpendChart } from './ChatCategorySpendChart';
import { resolveFollowUpPrompts } from '../../utils/mojo-follow-up-prompts';

const chatHistoryService = new AiChatHistoryService();

const SUGGESTED_PROMPTS = [
  'How can I save more?',
  'Analyse my spending',
  'Budget tips',
];

/** Status copy shown in-stream while waiting for the edge tool-calling loop. */
const AGENT_WORKING_STEPS = [
  'Reading your question…',
  'Looking up your financial data…',
  'Checking transactions and date range…',
  'Putting your answer together…',
];

interface MojoAgentPanelProps {
  onClose: () => void;
  className?: string;
}

export function MojoAgentPanel({ onClose, className = '' }: MojoAgentPanelProps): React.ReactElement {
  const { layoutMode, setLayoutMode, panelWidth, setPanelWidth } = useMojoAgent();
  const isPinned = layoutMode === 'push';
  const isWide = panelWidth === 'wide';
  const [userId, setUserId] = React.useState<string | null>(null);
  const [input, setInput] = React.useState('');
  const [sending, setSending] = React.useState(false);
  const [agentStatusIndex, setAgentStatusIndex] = React.useState(0);
  const [providerError, setProviderError] = React.useState<{ message: string; retry_after?: number } | null>(null);
  const [menuOpen, setMenuOpen] = useState(false);
  const [isExporting, setIsExporting] = useState(false);
  const [isClearing, setIsClearing] = useState(false);
  const messagesContainerRef = React.useRef<HTMLDivElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);
  const closeButtonRef = useRef<HTMLButtonElement>(null);
  const isInitialHistoryLoadRef = React.useRef(true);
  const queryClient = useQueryClient();

  React.useEffect(() => {
    getCurrentUserId().then(setUserId);
  }, []);

  React.useEffect(() => {
    closeButtonRef.current?.focus({ preventScroll: true });
  }, []);

  const scrollMessagesToBottom = React.useCallback((behavior: ScrollBehavior = 'smooth') => {
    const container = messagesContainerRef.current;
    if (!container) return;
    if (typeof container.scrollTo === 'function') {
      container.scrollTo({ top: container.scrollHeight, behavior });
    } else {
      container.scrollTop = container.scrollHeight;
    }
  }, []);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        setMenuOpen(false);
      }
    };
    if (menuOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [menuOpen]);

  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && menuOpen) setMenuOpen(false);
    };
    if (menuOpen) {
      document.addEventListener('keydown', handleEscape);
      return () => document.removeEventListener('keydown', handleEscape);
    }
  }, [menuOpen]);

  const { data: messages = [], isLoading } = useQuery({
    queryKey: ['insights-chat-history', userId],
    queryFn: () => chatHistoryService.getHistory(userId!),
    enabled: !!userId,
  });

  const {
    isLoading: settingsPending,
    aiEnabled: aiConfigured,
    settings,
    hasKey,
    underLimit,
  } = useIsAiOperational(userId);
  const aiEnabled = !!(userId && aiConfigured);
  const statusLoaded = !settingsPending && !!userId;
  const statusLabel = !statusLoaded ? '…' : aiEnabled ? 'Enabled' : 'Disabled';
  const statusTitle =
    !aiEnabled && settings
      ? settings.provider === 'none'
        ? 'AI is disabled in Settings'
        : !hasKey
          ? 'Add an API key in Settings → AI Configuration'
          : !underLimit
            ? 'Monthly limit reached. Adjust in Settings → AI Configuration.'
            : 'AI is disabled'
      : undefined;

  React.useEffect(() => {
    if (!sending) {
      setAgentStatusIndex(0);
      return;
    }
    setAgentStatusIndex(0);
    const id = window.setInterval(() => {
      setAgentStatusIndex((i) => (i + 1) % AGENT_WORKING_STEPS.length);
    }, 2800);
    return () => window.clearInterval(id);
  }, [sending]);

  const prevMessagesLengthRef = React.useRef<number | undefined>(undefined);
  React.useEffect(() => {
    if (isLoading) return;

    const len = (messages?.length ?? 0) + (sending ? 1 : 0);

    if (isInitialHistoryLoadRef.current) {
      isInitialHistoryLoadRef.current = false;
      prevMessagesLengthRef.current = len;
      return;
    }

    const prevLen = prevMessagesLengthRef.current;
    const lengthIncreased = prevLen !== undefined && len > prevLen;
    prevMessagesLengthRef.current = len;

    if (!lengthIncreased && !sending) {
      return;
    }

    scrollMessagesToBottom('smooth');
  }, [messages, sending, isLoading, scrollMessagesToBottom]);

  const sendChatMessage = async (rawText: string) => {
    const text = rawText.trim();
    if (!text || !userId || sending || !aiEnabled) return;

    const optimisticId = `optimistic-user-${Date.now()}`;
    const optimisticUser: ChatMessageRow = {
      id: optimisticId,
      role: 'user',
      content: text,
      created_at: new Date().toISOString(),
    };
    const previous =
      queryClient.getQueryData<ChatMessageRow[]>(['insights-chat-history', userId]) ?? [];

    setInput('');
    setProviderError(null);
    setSending(true);
    queryClient.setQueryData<ChatMessageRow[]>(['insights-chat-history', userId], [
      ...previous,
      optimisticUser,
    ]);

    try {
      const result = await sendMessage(userId, text);
      if (result && 'error' in result && result.error === 'provider_unavailable') {
        queryClient.setQueryData(['insights-chat-history', userId], previous);
        setProviderError({
          message: result.message ?? 'The AI service is temporarily unavailable. Please try again.',
          retry_after: result.retry_after,
        });
        return;
      }
      if (result && 'error' in result && result.error === 'monthly_limit_reached') {
        queryClient.setQueryData(['insights-chat-history', userId], previous);
        setProviderError({
          message: result.message ?? 'Your monthly AI spend limit has been reached. Adjust it in Settings → AI Configuration, or try again next month.',
        });
        return;
      }
      if (result && 'assistant_message' in result) {
        const assistant = result.assistant_message;
        if (assistant) {
          queryClient.setQueryData<ChatMessageRow[]>(['insights-chat-history', userId], [
            ...previous,
            {
              id: `temp-user-${Date.now()}`,
              role: 'user',
              content: text,
              created_at: optimisticUser.created_at,
            },
            {
              id: assistant.id ?? `temp-assistant-${Date.now()}`,
              role: assistant.role ?? 'assistant',
              content: assistant.content,
              created_at: assistant.created_at ?? new Date().toISOString(),
              ...(assistant.metadata != null ? { metadata: assistant.metadata } : {}),
            },
          ]);
        }
        await queryClient.invalidateQueries({ queryKey: ['insights-chat-history', userId] });
      }
    } catch (err) {
      queryClient.setQueryData(['insights-chat-history', userId], previous);
      const message = err instanceof Error ? err.message : 'Something went wrong. Please try again.';
      setProviderError({ message });
    } finally {
      setSending(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    await sendChatMessage(input);
  };

  const handleSuggestedPrompt = (prompt: string) => {
    setInput(prompt);
  };

  const handleFollowUpPrompt = (prompt: string) => {
    void sendChatMessage(prompt);
  };

  const handleClearHistory = async () => {
    if (!userId) return;
    setMenuOpen(false);
    setIsClearing(true);
    try {
      await chatHistoryService.clearHistory(userId);
      await queryClient.invalidateQueries({ queryKey: ['insights-chat-history', userId] });
    } finally {
      setIsClearing(false);
    }
  };

  const handleExportHistory = async () => {
    if (!userId || messages.length === 0) return;
    setMenuOpen(false);
    setIsExporting(true);
    try {
      const data = await chatHistoryService.exportHistory(userId);
      const payload = { messages: data ?? [] };
      const blob = new Blob([JSON.stringify(payload, null, 2)], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `ai-chat-history-${format(new Date(), 'yyyy-MM-dd')}.json`;
      a.click();
      URL.revokeObjectURL(url);
    } finally {
      setIsExporting(false);
    }
  };

  const hasMessages = (messages?.length ?? 0) > 0;

  const latestAssistantId = React.useMemo(() => {
    for (let i = messages.length - 1; i >= 0; i--) {
      if (messages[i].role === 'assistant') return messages[i].id;
    }
    return null;
  }, [messages]);

  return (
    <div
      className={`flex flex-col h-full bg-white dark:bg-gray-800 overflow-hidden ${className}`}
      aria-label="Mojo Agent"
    >
      <div className="flex items-center gap-2 px-4 py-3 border-b border-gray-200 dark:border-gray-700">
        <Bot className="h-5 w-5 text-purple-600 dark:text-purple-400" aria-hidden />
        <h2 className="text-base font-semibold text-gray-900 dark:text-gray-100">Mojo Agent</h2>
        <div className="relative flex items-center gap-1 ml-auto" ref={menuRef}>
          <span
            className={`flex items-center gap-1 text-xs ${
              !statusLoaded
                ? 'text-gray-500 dark:text-gray-400'
                : aiEnabled
                  ? 'text-green-600 dark:text-green-400'
                  : 'text-gray-500 dark:text-gray-400'
            }`}
            title={statusTitle}
            aria-label={
              statusTitle
                ? `AI assistant: ${statusLabel}. ${statusTitle}`
                : `AI assistant: ${statusLabel}`
            }
          >
            <span
              className={`h-2 w-2 rounded-full ${
                !statusLoaded
                  ? 'bg-gray-400 dark:bg-gray-500'
                  : aiEnabled
                    ? 'bg-green-500'
                    : 'bg-gray-400 dark:bg-gray-500'
              }`}
              aria-hidden
            />
            {statusLabel}
          </span>
          <button
            type="button"
            onClick={() => setPanelWidth(isWide ? 'default' : 'wide')}
            className="hidden lg:inline-flex shrink-0 p-1.5 rounded-md text-gray-500 hover:text-gray-700 hover:bg-gray-100 dark:text-gray-400 dark:hover:text-gray-200 dark:hover:bg-gray-700 focus-ring disabled:opacity-40 disabled:cursor-not-allowed"
            aria-label={isWide ? 'Collapse panel width' : 'Expand panel to 90%'}
            aria-pressed={isWide}
            title={
              isPinned
                ? 'Unpin the panel to expand width'
                : isWide
                  ? 'Collapse panel width'
                  : 'Expand panel to 90%'
            }
            disabled={isPinned}
            data-testid="mojo-agent-width-toggle"
          >
            {isWide && !isPinned ? (
              <Minimize2 className="h-5 w-5" aria-hidden />
            ) : (
              <Maximize2 className="h-5 w-5" aria-hidden />
            )}
          </button>
          <button
            type="button"
            onClick={() => setLayoutMode(isPinned ? 'overlay' : 'push')}
            className="hidden lg:inline-flex shrink-0 p-1.5 rounded-md text-gray-500 hover:text-gray-700 hover:bg-gray-100 dark:text-gray-400 dark:hover:text-gray-200 dark:hover:bg-gray-700 focus-ring"
            aria-label={isPinned ? 'Unpin panel' : 'Pin panel'}
            aria-pressed={isPinned}
            title={isPinned ? 'Unpin panel' : 'Pin panel'}
            data-testid="mojo-agent-pin-toggle"
          >
            {isPinned ? (
              <PinOff className="h-5 w-5" aria-hidden />
            ) : (
              <Pin className="h-5 w-5" aria-hidden />
            )}
          </button>
          <button
            type="button"
            onClick={() => setMenuOpen((open) => !open)}
            className="p-1.5 rounded-md text-gray-500 hover:text-gray-700 hover:bg-gray-100 dark:text-gray-400 dark:hover:text-gray-200 dark:hover:bg-gray-700 focus-ring"
            aria-label="Chat menu"
            aria-expanded={menuOpen}
            aria-haspopup="menu"
          >
            <MoreVertical className="h-5 w-5" aria-hidden />
          </button>
          {menuOpen && (
            <div
              className="absolute right-0 top-full mt-1 w-56 bg-white dark:bg-gray-800 rounded-lg shadow-lg border border-gray-200 dark:border-gray-700 z-[100] py-1"
              role="menu"
              aria-label="Chat menu"
            >
              <button
                type="button"
                onClick={handleClearHistory}
                disabled={!userId || !hasMessages || isClearing}
                className="w-full flex items-center gap-3 px-4 py-2.5 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700/50 disabled:opacity-50 disabled:pointer-events-none"
                role="menuitem"
                aria-label="Clear history"
              >
                <Trash2 className="h-4 w-4 flex-shrink-0" aria-hidden />
                <span>Clear history</span>
              </button>
              <button
                type="button"
                onClick={handleExportHistory}
                disabled={!hasMessages || isExporting}
                className="w-full flex items-center gap-3 px-4 py-2.5 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700/50 disabled:opacity-50 disabled:pointer-events-none"
                role="menuitem"
                aria-label="Export chat"
              >
                <Download className="h-4 w-4 flex-shrink-0" aria-hidden />
                <span>Export chat</span>
              </button>
              <Link
                to="/settings/ai"
                onClick={() => setMenuOpen(false)}
                className="w-full flex items-center gap-3 px-4 py-2.5 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700/50"
                role="menuitem"
                aria-label="View AI Configuration"
              >
                <Bot className="h-4 w-4 flex-shrink-0" aria-hidden />
                <span>View AI Configuration</span>
              </Link>
            </div>
          )}
          <button
            ref={closeButtonRef}
            type="button"
            onClick={onClose}
            className="p-1.5 rounded-md text-gray-500 hover:text-gray-700 hover:bg-gray-100 dark:text-gray-400 dark:hover:text-gray-200 dark:hover:bg-gray-700 focus-ring"
            aria-label="Close Mojo Agent"
          >
            <X className="h-5 w-5" aria-hidden />
          </button>
        </div>
      </div>

      {statusLoaded && !aiEnabled && (
        <div
          className="flex items-center gap-2 px-4 py-2 bg-gray-100 dark:bg-gray-700/50 border-b border-gray-200 dark:border-gray-600 text-gray-700 dark:text-gray-300 text-sm"
          role="status"
          aria-live="polite"
        >
          <AlertCircle className="h-4 w-4 flex-shrink-0" aria-hidden />
          <span className="flex-1 min-w-0">{statusTitle ?? 'AI assistant is disabled.'}</span>
          <Link
            to="/settings/ai"
            className="flex-shrink-0 font-medium text-primary-600 dark:text-primary-400 hover:underline focus-ring rounded"
          >
            Settings
          </Link>
        </div>
      )}

      {aiEnabled && providerError && (
        <div
          className="flex items-center gap-2 px-4 py-2 bg-amber-50 dark:bg-amber-900/20 border-b border-amber-200 dark:border-amber-800 text-amber-800 dark:text-amber-200 text-sm"
          role="alert"
        >
          <AlertCircle className="h-4 w-4 flex-shrink-0" aria-hidden />
          <span className="flex-1 min-w-0">{providerError.message}</span>
          <button type="button" onClick={() => setProviderError(null)} className="btn-secondary btn-sm flex-shrink-0">
            Retry
          </button>
        </div>
      )}

      <div ref={messagesContainerRef} className="flex-1 min-h-0 overflow-y-auto p-4 space-y-4">
        {isLoading ? (
          <div className="flex items-center justify-center py-6">
            <Loader2 className="h-5 w-5 animate-spin text-gray-400" aria-label="Loading messages" />
          </div>
        ) : messages.length === 0 ? (
          <>
            <div className="flex items-start gap-2">
              <Bot className="h-5 w-5 text-purple-600 dark:text-purple-400 mt-0.5 flex-shrink-0" aria-hidden />
              <div>
                <p className="text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">Mojo Agent</p>
                <p className="text-sm text-gray-700 dark:text-gray-300">
                  Hi I&apos;m your financial assistant. I can help you understand your spending patterns, suggest
                  ways to save money and answer questions about your finances. What would you like to know?
                </p>
              </div>
            </div>
            <div className="flex flex-wrap gap-2">
              {SUGGESTED_PROMPTS.map((prompt) => (
                <button
                  key={prompt}
                  type="button"
                  onClick={() => handleSuggestedPrompt(prompt)}
                  disabled={!aiEnabled}
                  className="rounded-full bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 px-3 py-1.5 text-sm hover:bg-gray-200 dark:hover:bg-gray-600 focus-ring disabled:opacity-60 disabled:cursor-not-allowed disabled:hover:bg-gray-100 dark:disabled:hover:bg-gray-700"
                >
                  {prompt}
                </button>
              ))}
            </div>
            <p className="text-xs text-gray-400 dark:text-gray-500">{format(new Date(), 'HH:mm')}</p>
          </>
        ) : (
          <>
            {messages.map((msg) => {
              const followUpPrompts =
                msg.role === 'assistant' && msg.id === latestAssistantId && !sending
                  ? resolveFollowUpPrompts(msg)
                  : [];
              const showFollowUps = followUpPrompts.length > 0;
              return (
            <div key={msg.id} className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}>
              <div
                className={`rounded-lg px-3 py-2 text-sm ${
                  msg.role === 'user'
                    ? 'max-w-[85%] bg-primary-600 text-white'
                    : 'max-w-full w-full bg-gray-100 dark:bg-gray-700 text-gray-900 dark:text-gray-100'
                }`}
              >
                {msg.role === 'assistant' ? (
                  <div className="chat-markdown">
                    <ReactMarkdown
                      remarkPlugins={[remarkGfm]}
                      components={{
                        p: ({ children }) => <p className="mb-2 last:mb-0 whitespace-pre-wrap">{children}</p>,
                        ul: ({ children }) => <ul className="list-disc list-inside mb-2 space-y-0.5">{children}</ul>,
                        ol: ({ children }) => <ol className="list-decimal list-inside mb-2 space-y-0.5">{children}</ol>,
                        li: ({ children }) => <li className="ml-2">{children}</li>,
                        strong: ({ children }) => <strong className="font-semibold">{children}</strong>,
                        pre: ({ children }) => (
                          <pre className="bg-gray-200 dark:bg-gray-600 rounded p-2 my-2 overflow-x-auto text-xs">
                            {children}
                          </pre>
                        ),
                        code: ({ className: codeClassName, children }) =>
                          codeClassName ? (
                            <code className={codeClassName}>{children}</code>
                          ) : (
                            <code className="bg-gray-200 dark:bg-gray-600 rounded px-1 py-0.5 text-xs">{children}</code>
                          ),
                        h1: ({ children }) => <h1 className="text-base font-bold mt-2 mb-1 first:mt-0">{children}</h1>,
                        h2: ({ children }) => <h2 className="text-sm font-bold mt-2 mb-1 first:mt-0">{children}</h2>,
                        h3: ({ children }) => <h3 className="text-sm font-semibold mt-2 mb-1 first:mt-0">{children}</h3>,
                        table: ({ children }) => (
                          <div className="chat-markdown-table-wrap my-2 overflow-x-auto rounded-md border border-gray-200 dark:border-gray-600">
                            <table className="chat-markdown-table w-full min-w-[20rem] border-collapse text-left text-xs">
                              {children}
                            </table>
                          </div>
                        ),
                        thead: ({ children }) => (
                          <thead className="bg-gray-200/80 dark:bg-gray-600/80">{children}</thead>
                        ),
                        tbody: ({ children }) => <tbody>{children}</tbody>,
                        tr: ({ children }) => (
                          <tr className="border-b border-gray-200 dark:border-gray-600 last:border-b-0">{children}</tr>
                        ),
                        th: ({ children }) => (
                          <th className="px-2.5 py-1.5 font-semibold text-gray-800 dark:text-gray-100 whitespace-nowrap">
                            {children}
                          </th>
                        ),
                        td: ({ children }) => (
                          <td className="px-2.5 py-1.5 text-gray-700 dark:text-gray-200 whitespace-nowrap align-top">
                            {children}
                          </td>
                        ),
                      }}
                    >
                      {msg.content}
                    </ReactMarkdown>
                    {msg.metadata?.charts?.[0]?.type === 'category_spend_bar' ? (
                      <ChatCategorySpendChart chart={msg.metadata.charts[0]} />
                    ) : null}
                    {showFollowUps ? (
                      <div className="flex flex-wrap gap-2 mt-2" role="group" aria-label="Suggested follow-up prompts">
                        {followUpPrompts.map((prompt) => (
                          <button
                            key={prompt}
                            type="button"
                            data-testid="mojo-suggested-prompt"
                            onClick={() => handleFollowUpPrompt(prompt)}
                            disabled={!aiEnabled}
                            className="rounded-full bg-white dark:bg-gray-600 border border-gray-200 dark:border-gray-500 text-gray-700 dark:text-gray-200 px-3 py-1.5 text-xs hover:bg-gray-50 dark:hover:bg-gray-500 focus-ring disabled:opacity-60 disabled:cursor-not-allowed"
                          >
                            {prompt}
                          </button>
                        ))}
                      </div>
                    ) : null}
                  </div>
                ) : (
                  <p className="whitespace-pre-wrap">{msg.content}</p>
                )}
                <p
                  className={`text-xs mt-1 ${msg.role === 'user' ? 'text-primary-100' : 'text-gray-500 dark:text-gray-400'}`}
                >
                  {msg.created_at ? format(new Date(msg.created_at), 'HH:mm') : ''}
                </p>
              </div>
            </div>
              );
            })}
            {sending && (
              <div className="flex justify-start" data-testid="mojo-agent-working">
                <div
                  className="max-w-[85%] rounded-lg px-3 py-2 text-sm bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-200"
                  role="status"
                  aria-live="polite"
                  aria-label="Mojo Agent is working"
                >
                  <div className="flex items-center gap-2">
                    <Loader2 className="h-4 w-4 animate-spin text-purple-600 dark:text-purple-400 flex-shrink-0" aria-hidden />
                    <p className="text-sm">{AGENT_WORKING_STEPS[agentStatusIndex]}</p>
                  </div>
                  <p className="text-xs mt-1 text-gray-500 dark:text-gray-400">Mojo Agent</p>
                </div>
              </div>
            )}
          </>
        )}
      </div>

      <form onSubmit={handleSubmit} className="p-4 border-t border-gray-200 dark:border-gray-700">
        <div className="flex gap-2 items-end">
          <textarea
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && !e.shiftKey) {
                e.preventDefault();
                void sendChatMessage(input);
              }
            }}
            rows={3}
            placeholder={aiEnabled ? 'Ask about your finances...' : 'Enable AI in Settings to chat'}
            className="flex-1 resize-none rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 px-3 py-2 text-sm leading-5 focus-ring disabled:opacity-60 disabled:cursor-not-allowed"
            disabled={sending || !userId || !aiEnabled}
            aria-label="Ask about your finances"
            aria-disabled={!aiEnabled}
          />
          <button
            type="submit"
            disabled={sending || !input.trim() || !userId || !aiEnabled}
            className="btn-primary btn-sm inline-flex items-center justify-center p-2 disabled:opacity-60 disabled:cursor-not-allowed"
            aria-label="Send message"
          >
            {sending ? (
              <Loader2 className="h-4 w-4 animate-spin" aria-hidden />
            ) : (
              <Send className="h-4 w-4" aria-hidden />
            )}
          </button>
        </div>
        <p className="text-xs text-gray-500 dark:text-gray-400 mt-2">
          AI responses are based on your financial data and patterns that are set for availability.
        </p>
      </form>
    </div>
  );
}
